import { AttachmentStrategy } from "@redotech/redo-storefront-common/attachment-strategy";
import { InfoModalVersion } from "@redotech/redo-storefront-common/info-modal-version";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import {
  ImageElementType,
  PlacementContainerType,
  PlacementType,
  PriceLocation,
  WidgetElementType,
} from "@redotech/redo-storefront-common/widget-config";
import { ButtonSettingPerProduct } from "@redotech/redo-storefront-widget-common/widget";
import { assertNever } from "@redotech/util/type";
import * as z from "zod";

const TitleSubtitleSchema = z.object({
  titleText: z.string(),
  subtitleText: z.string(),
});

const CoverageTextSchema = z.object({
  returns: TitleSubtitleSchema,
  packageProtection: TitleSubtitleSchema,
  both: TitleSubtitleSchema,
});

const PlacementDescriptorSchema = z.object({
  cssSelector: z.string().trim(),
  insertionPosition: z.nativeEnum(InsertionPosition),
  placementContainerType: z.nativeEnum(PlacementContainerType),
  hidingElementSelector: z.string(),
  shadowRootSelector: z.string(),
});

const CommonWidgetFields = {
  placementSelector: z.string().trim(),
  insertionPosition: z.nativeEnum(InsertionPosition),
  shadowRootSelector: z.string(),
  placeInsideAndOutsideShadowRoot: z.boolean(),
  dataTarget: z.string(),
  placements: z.array(PlacementDescriptorSchema),
};

const ClickStandardCheckoutButtonSchema = z.object({
  enabled: z.boolean(),
  checkoutButtonSelector: z.string(),
});

const InfoCardSchema = z.object({
  ...CommonWidgetFields,
  cardText: CoverageTextSchema,
  showInfoIcon: z.boolean(),
  infoCardStyles: z.object({
    color: z.string(),
    titleFontSize: z.number(),
    titleFontColor: z.string(),
    subtitleFontSize: z.number(),
    subtitleFontColor: z.string(),
  }),
  badgeWord: z.string(),
  imageElement: z.nativeEnum(ImageElementType),
  priceLocation: z.nativeEnum(PriceLocation),
  showBackground: z.boolean(),
});

const BothCheckoutButtonsSchema = z.object({
  ...CommonWidgetFields,
  coverageButtonText: CoverageTextSchema,
  nonCoverageButtonText: z.object({
    returns: z.string(),
    packageProtection: z.string(),
    both: z.string(),
  }),
  coverageButtonStyles: z.object({
    color: z.string(),
    fontColor: z.string(),
    subtitleFontColor: z.string(),
    fontSize: z.number(),
    subtitleFontSize: z.number(),
    nonCoverageFontColor: z.string(),
    nonCoverageFontSize: z.number(),
    includeNonCoverageUnderline: z.boolean(),
  }),
  buttonSettingPerProduct: z.nativeEnum(ButtonSettingPerProduct),
  optOutButtonFirst: z.boolean(),
  clickStandardCheckoutButton: ClickStandardCheckoutButtonSchema,
});

const IndividualCheckoutButtonSchema = z.object({
  ...CommonWidgetFields,
  content: z.string(),
  clickStandardCheckoutButton: ClickStandardCheckoutButtonSchema,
});

const CartCheckboxSchema = z.object({ ...CommonWidgetFields });

const WidgetConfigItemBaseSchema = z.object({
  name: z.string().optional(),
  placementType: z.nativeEnum(PlacementType),
});

const WidgetConfigItemInputSchema = WidgetConfigItemBaseSchema.extend({
  type: z.nativeEnum(WidgetElementType),
  config: z.object({
    infoCardConfig: InfoCardSchema.optional(),
    bothCheckoutButtonsConfig: BothCheckoutButtonsSchema.optional(),
    individualCheckoutButtonConfig: IndividualCheckoutButtonSchema.optional(),
    cartCheckboxConfig: CartCheckboxSchema.optional(),
  }),
});

/** Element types the onboarding coverage builder edits; others are preserved
 *  server-side so an admin-created widget never blocks or gets lost on save. */
export const RETURNS_COVERAGE_BUILDER_WIDGET_TYPES: ReadonlySet<WidgetElementType> =
  new Set([
    WidgetElementType.INFO_CARD,
    WidgetElementType.BOTH_CHECKOUT_BUTTONS,
    WidgetElementType.COVERAGE_CHECKOUT_BUTTON,
    WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON,
    WidgetElementType.CART_CHECKBOX,
  ]);

const PersistedWidgetConfigItemSchema = z.discriminatedUnion("type", [
  WidgetConfigItemBaseSchema.extend({
    type: z.literal(WidgetElementType.INFO_CARD),
    config: z.object({ infoCardConfig: InfoCardSchema }),
  }),
  WidgetConfigItemBaseSchema.extend({
    type: z.literal(WidgetElementType.BOTH_CHECKOUT_BUTTONS),
    config: z.object({ bothCheckoutButtonsConfig: BothCheckoutButtonsSchema }),
  }),
  WidgetConfigItemBaseSchema.extend({
    type: z.literal(WidgetElementType.COVERAGE_CHECKOUT_BUTTON),
    config: z.object({
      individualCheckoutButtonConfig: IndividualCheckoutButtonSchema,
    }),
  }),
  WidgetConfigItemBaseSchema.extend({
    type: z.literal(WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON),
    config: z.object({
      individualCheckoutButtonConfig: IndividualCheckoutButtonSchema,
    }),
  }),
  WidgetConfigItemBaseSchema.extend({
    type: z.literal(WidgetElementType.CART_CHECKBOX),
    config: z.object({ cartCheckboxConfig: CartCheckboxSchema }),
  }),
]);

function persistedWidgetConfigPlacements(
  item: z.infer<typeof PersistedWidgetConfigItemSchema>,
) {
  switch (item.type) {
    case WidgetElementType.INFO_CARD:
      return item.config.infoCardConfig.placements;
    case WidgetElementType.BOTH_CHECKOUT_BUTTONS:
      return item.config.bothCheckoutButtonsConfig.placements;
    case WidgetElementType.COVERAGE_CHECKOUT_BUTTON:
    case WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON:
      return item.config.individualCheckoutButtonConfig.placements;
    case WidgetElementType.CART_CHECKBOX:
      return item.config.cartCheckboxConfig.placements;
    default:
      return assertNever(item);
  }
}

const PersistedWidgetConfigItemWithPlacementSchema =
  PersistedWidgetConfigItemSchema.superRefine((item, ctx) => {
    const placements = persistedWidgetConfigPlacements(item);
    if (
      placements.length > 0 &&
      placements.every((placement) => placement.cssSelector.length > 0)
    ) {
      return;
    }
    ctx.addIssue({
      code: "custom",
      message: "A verified placement selector is required",
      path: ["config"],
    });
  });

const PersistedWidgetConfigItemWithAutomaticPlacementSchema =
  PersistedWidgetConfigItemSchema.superRefine((item, ctx) => {
    if (persistedWidgetConfigPlacements(item).length === 0) {
      return;
    }
    ctx.addIssue({
      code: "custom",
      message: "Automatic placement cannot include custom selectors",
      path: ["config"],
    });
  });

const ToggleFieldsSchema = z.object({
  titleText: z.string(),
  positiveSubtext: z.string(),
  negativeSubtext: z.string(),
});

const InfoModalSchema = z.object({
  cartIconEnabled: z.boolean(),
  checkoutIconEnabled: z.boolean(),
  enableTopLayerModalApi: z.boolean(),
  logo: z.string(),
  sideImage: z.string(),
  version: z.nativeEnum(InfoModalVersion),
  headerText: z.string(),
  subheaderText: z.string(),
  line1: TitleSubtitleSchema,
  line2: TitleSubtitleSchema,
  line3: TitleSubtitleSchema,
  customCss: z.string(),
});

const ReturnsCoverageBuilderEditableValueSchema = z.object({
  checkbox: z.object({
    enabled: z.boolean(),
    color: z.string(),
    selector: z.string(),
    fontSize: z
      .string()
      .trim()
      .min(1)
      .refine((value) => Number.isFinite(Number(value)), {
        message: "Font size must be numeric",
      }),
    text: z.string(),
    textOverride: z.string(),
    textAfterRedo: z.string(),
    showPrice: z.boolean(),
  }),
  widgetConfigs: z.array(WidgetConfigItemInputSchema),
  cartElementImage: z.string().url().optional(),
  useShadowRootInCart: z.boolean(),
  cartShadowRootSelector: z.string(),
  toggleTextOptions: z.object({ returnToggle: ToggleFieldsSchema }),
  toggleOnColor: z.string(),
  toggleOffColor: z.string(),
  toggleCircleColor: z.string(),
  cartToggleSelector: z.string(),
  cartToggleShadowRootSelector: z.string(),
  cartTogglePlacement: z.nativeEnum(InsertionPosition),
  infoModal: InfoModalSchema,
});

export const ReturnsCoverageBuilderInitialValueSchema =
  ReturnsCoverageBuilderEditableValueSchema.extend({
    attachmentStrategy: z.nativeEnum(AttachmentStrategy),
  });

export const ReturnsCoverageBuilderPlacementModeSchema = z.enum([
  "automatic",
  "manual",
]);
export type ReturnsCoverageBuilderPlacementMode = z.infer<
  typeof ReturnsCoverageBuilderPlacementModeSchema
>;

export const ReturnsCoverageBuilderSaveValueSchema = z.discriminatedUnion(
  "placementMode",
  [
    ReturnsCoverageBuilderEditableValueSchema.extend({
      placementMode: z.literal("automatic"),
      widgetConfigs: z.array(
        PersistedWidgetConfigItemWithAutomaticPlacementSchema,
      ),
    }),
    ReturnsCoverageBuilderEditableValueSchema.extend({
      placementMode: z.literal("manual"),
      widgetConfigs: z.array(PersistedWidgetConfigItemWithPlacementSchema),
    }),
  ],
);

export const saveReturnsCoverageBuilder = {
  name: "saveReturnsCoverageBuilder" as const,
  input: ReturnsCoverageBuilderSaveValueSchema,
  output: z.void(),
};
